export type AtlasPlanningErrorCategory =
  | 'correction_required'
  | 'permission'
  | 'stale_draft'
  | 'temporary'
  | 'unknown_outcome'
  | 'source'
  | 'unavailable';

export type AtlasPlanningRecoveryAction =
  | 'retry'
  | 'return_to_editing'
  | 'check_status'
  | 'review_latest'
  | 'request_access'
  | 'upgrade'
  | 'discard'
  | null;

export interface AtlasPlanningErrorIssue {
  readonly code?: string;
  readonly message?: string;
  readonly severity?: 'error' | 'warning';
}

export interface AtlasPlanningErrorInput {
  readonly status?: number;
  readonly code?: string;
  readonly message?: string;
  readonly retryable?: boolean;
  readonly outcome?: 'not_prepared' | 'unknown';
  readonly issues?: readonly AtlasPlanningErrorIssue[];
  readonly blockedReason?: string;
  readonly blockedDetail?: string;
}

export interface AtlasPlanningErrorPresentation {
  readonly title: string;
  readonly message: string;
  readonly category: AtlasPlanningErrorCategory;
  readonly action: AtlasPlanningRecoveryAction;
  readonly retryable: boolean;
  readonly draftState: 'saved_not_applied' | 'not_saved' | 'unknown';
}

export function presentAtlasPlanningError(
  input: AtlasPlanningErrorInput,
  context: { readonly operation: 'review' | 'prepare' | 'apply' | 'discard' | 'status' | 'save'; readonly draftSaved?: boolean } = { operation: 'review' },
): AtlasPlanningErrorPresentation {
  const code = input.code ?? '';
  const draftState = input.outcome === 'unknown'
    ? 'unknown'
    : context.draftSaved === false ? 'not_saved' : 'saved_not_applied';
  const issue = input.issues?.find((candidate) => candidate.severity !== 'warning')
    ?? input.issues?.[0];

  if (input.outcome === 'unknown' || code === 'intent_in_progress' || code === 'primary_source_writeback_outcome_unknown') {
    return {
      title: 'We couldn’t confirm what happened',
      message: 'The request may still be processing. Check the operation status before retrying so the same change is not submitted twice.',
      category: 'unknown_outcome', action: 'check_status', retryable: false, draftState: 'unknown',
    };
  }

  if (code === 'authorization_denied' || code === 'folder_access_denied' || code === 'scenario_scope_forbidden'
    || code === 'access_context_not_found' || input.status === 401 || input.status === 403) {
    return {
      title: 'Permission needed',
      message: context.operation === 'prepare'
        ? 'We couldn’t verify your permission to prepare this live review. Your draft is still saved and no changes were applied. Check your Full Org Chart edit access, then try again.'
        : 'You no longer have permission to complete this organization change. Your draft is still saved and no changes were applied. Check your access or ask a workspace administrator for help.',
      category: 'permission', action: 'request_access', retryable: false, draftState,
    };
  }

  if (code === 'upgrade_required' || code === 'PLAN_UPGRADE_REQUIRED' || code === 'ENTERPRISE_PRODUCT_GRANT_REQUIRED') {
    return {
      title: 'A plan upgrade is required',
      message: 'This planning action is not available on your current Atlas plan. Your draft is still saved and no changes were applied.',
      category: 'permission', action: 'upgrade', retryable: false, draftState,
    };
  }

  if (code === 'change_set_not_found' || input.status === 404) {
    return {
      title: 'This draft is no longer available',
      message: 'The saved draft may have been discarded or replaced. Refresh the plans list and open the latest draft.',
      category: 'unavailable', action: 'review_latest', retryable: false, draftState,
    };
  }

  if (code === 'graph_version_conflict' || code === 'source_version_conflict'
    || code === 'authorization_policy_version_conflict' || code === 'scenario_revision_conflict') {
    return {
      title: 'The organization changed while you were working',
      message: 'This draft is based on an older version of the organization. Review the latest chart and reprepare the change before trying again.',
      category: 'stale_draft', action: 'review_latest', retryable: false, draftState,
    };
  }

  if (issue?.message || code === 'invalid_post_state' || code === 'invalid_change_set' || code === 'invalid_change_set_payload'
    || code === 'operational_execution_blocked' || code === 'projection_incomplete'
    || code === 'source_mapping_not_roundtrip_safe' || code === 'source_mapping_partially_covers_change') {
    const detail = issue?.message ?? input.blockedDetail;
    return {
      title: 'This change needs attention',
      message: detail
        ? `The draft contains a change that cannot be completed: ${detail} Return to editing to correct it.`
        : 'The draft contains a change that cannot be completed with the current organization data. Return to editing to correct it, then review again.',
      category: 'correction_required', action: 'return_to_editing', retryable: false, draftState,
    };
  }

  if (code === 'primary_source_writeback_failed' || code === 'primary_source_writeback_partially_applied') {
    return {
      title: code === 'primary_source_writeback_partially_applied' ? 'The source accepted only part of the change' : 'The primary source rejected the change',
      message: code === 'primary_source_writeback_partially_applied'
        ? 'Atlas was not changed. Reconcile the affected source rows before starting another change.'
        : 'Atlas was not changed. Check the source connection and permissions, then try again.',
      category: 'source', action: 'review_latest', retryable: false, draftState,
    };
  }

  if (input.retryable || input.status === 408 || input.status === 429 || input.status === 500 || input.status === 502 || input.status === 503 || input.status === 504) {
    return {
      title: 'The review is temporarily unavailable',
      message: 'Your draft is safe and no changes were applied. Try again in a moment.',
      category: 'temporary', action: 'retry', retryable: true, draftState,
    };
  }

  return {
    title: 'We couldn’t complete this planning action',
    message: context.operation === 'save'
      ? 'The change was not saved. Try again, and keep this window open while it finishes.'
      : 'Your draft is still saved and no changes were applied. Try the action again, or return to editing to review the draft.',
    category: 'unavailable', action: context.operation === 'save' ? 'retry' : 'return_to_editing',
    retryable: context.operation === 'save', draftState,
  };
}

export function atlasPlanningErrorInput(error: unknown): AtlasPlanningErrorInput {
  if (!error || typeof error !== 'object') {
    return { message: error instanceof Error ? error.message : undefined };
  }
  const value = error as Record<string, unknown>;
  const details = value.details && typeof value.details === 'object'
    ? value.details as Record<string, unknown>
    : {};
  return {
    status: typeof value.status === 'number' ? value.status : undefined,
    code: typeof value.code === 'string' ? value.code
      : typeof value.serverCode === 'string' ? value.serverCode
        : typeof details.code === 'string' ? details.code : undefined,
    message: error instanceof Error ? error.message : typeof value.message === 'string' ? value.message : undefined,
    retryable: value.retryable === true,
    outcome: value.outcome === 'unknown' || value.outcome === 'not_prepared' ? value.outcome : undefined,
    issues: Array.isArray(value.issues) ? value.issues as AtlasPlanningErrorIssue[]
      : Array.isArray(details.issues) ? details.issues as AtlasPlanningErrorIssue[] : undefined,
    blockedReason: typeof value.blockedReason === 'string' ? value.blockedReason
      : typeof details.blockedReason === 'string' ? details.blockedReason : undefined,
    blockedDetail: typeof value.blockedDetail === 'string' ? value.blockedDetail
      : typeof details.blockedDetail === 'string' ? details.blockedDetail : undefined,
  };
}
